import { useState, useEffect } from 'react';
import { api } from '../services/api';

/**
 * Renders and saves custom field values for a given entity.
 *
 * Props:
 *   entityType  — 'lead' | 'conta'
 *   entityId    — numeric id (null = new entity, won't load/save values)
 *   onChange    — (fieldId, value) => void  — called on every change so parent can collect values
 *   readOnly    — boolean
 */
export default function CustomFieldsSection({ entityType, entityId, onChange, readOnly = false }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({}); // { field_id: value }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCustomFields(entityType)
      .then(setFields)
      .catch(() => setFields([]))
      .finally(() => setLoading(false));
  }, [entityType]);

  useEffect(() => {
    if (!entityId || !fields.length) return;
    api.getCustomFieldValues(entityId, entityType)
      .then(rows => {
        const map = {};
        rows.forEach(r => { map[r.custom_field_id] = r.value; });
        setValues(map);
      })
      .catch(() => {});
  }, [entityId, entityType, fields.length]);

  function handleChange(field, rawValue) {
    setValues(prev => ({ ...prev, [field.id]: rawValue }));
    if (onChange) onChange(field.id, rawValue);
  }

  // Save all values for an existing entity
  async function saveValues(id) {
    if (!id || !fields.length) return;
    const items = fields
      .filter(f => values[f.id] !== undefined)
      .map(f => ({ field_id: f.id, entity_id: id, value: String(values[f.id] ?? '') }));
    if (items.length) await api.saveCustomFieldValues(items);
  }

  // Expose saveValues via ref — parent calls section.current.save(leadId)
  // We attach it to the DOM via a hidden trick: expose as a property on a wrapper ref
  // Pattern: parent wraps this with useImperativeHandle or simply calls api directly.
  // Here we use a simpler approach: parent reads pendingValues from onValuesReady callback.

  if (loading || !fields.length) return null;

  return (
    <div className="mt-4 pt-4 border-t">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
        Campos customizados
      </h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map(field => (
          <CustomFieldInput
            key={field.id}
            field={field}
            value={values[field.id] ?? ''}
            onChange={val => handleChange(field, val)}
            readOnly={readOnly}
          />
        ))}
      </div>
      {/* Hidden save trigger — parent component should call saveValues(entityId) */}
      <SaveTrigger onMount={fn => { CustomFieldsSection._saveRef = fn; }} saveValues={saveValues} />
    </div>
  );
}

// Internal save trigger pattern — parent will call CustomFieldsSection.save(id)
function SaveTrigger({ onMount, saveValues }) {
  useEffect(() => { onMount(saveValues); }, [saveValues]);
  return null;
}

// Static method so parent can call save without a ref
CustomFieldsSection.save = async (entityId) => {
  if (CustomFieldsSection._saveRef) {
    return CustomFieldsSection._saveRef(entityId);
  }
};

function CustomFieldInput({ field, value, onChange, readOnly }) {
  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.display_name}
      {field.required ? <span className="text-red-500 ml-1">*</span> : null}
    </label>
  );

  const baseClass = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50';

  switch (field.field_type) {
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return (
        <div>
          {label}
          <input
            type={field.field_type === 'phone' ? 'tel' : field.field_type}
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={readOnly}
            className={baseClass}
          />
        </div>
      );

    case 'number':
      return (
        <div>
          {label}
          <input
            type="number"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={readOnly}
            className={baseClass}
          />
        </div>
      );

    case 'date':
      return (
        <div>
          {label}
          <input
            type="date"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={readOnly}
            className={baseClass}
          />
        </div>
      );

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`cf_${field.id}`}
            checked={value === 'true' || value === true}
            onChange={e => onChange(String(e.target.checked))}
            disabled={readOnly}
            className="w-4 h-4 rounded"
          />
          <label htmlFor={`cf_${field.id}`} className="text-sm font-medium text-gray-700">
            {field.display_name}
          </label>
        </div>
      );

    case 'select': {
      const options = typeof field.options === 'string' ? JSON.parse(field.options || '[]') : (field.options || []);
      return (
        <div>
          {label}
          <select value={value} onChange={e => onChange(e.target.value)} disabled={readOnly} className={baseClass}>
            <option value="">Selecione...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      );
    }

    case 'multiselect': {
      const options = typeof field.options === 'string' ? JSON.parse(field.options || '[]') : (field.options || []);
      const selected = value ? (typeof value === 'string' ? value.split(',').filter(Boolean) : value) : [];
      function toggleOpt(opt) {
        const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
        onChange(next.join(','));
      }
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-2">
            {options.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => !readOnly && toggleOpt(opt)}
                className={`px-2 py-1 rounded text-xs border transition-colors ${
                  selected.includes(opt)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
